import React from 'react';
import { Box, Flex, Heading, Text, Image, Button } from 'rebass';
import styled from 'styled-components';
import Frame from 'components/Frame';
import Link from 'components/Link';
import SkillBox from 'components/SkillBox';
import { EMPLOYER } from 'constants/index';
import candidateLinkedInIcon from 'assets/icons/candidateLinkedIn.png';
import candidateTwitterIcon from 'assets/icons/candidateTwitter.png';
import candidateGitHubIcon from 'assets/icons/candidateGitHub.png';
import candidateWebsiteIcon from 'assets/icons/candidateWebsite.png';
import colors from 'theme/colors';
import Icon from 'components/Icon';
import useCandidateDetails from 'modules/Candidates/hooks/useCandidateDetails';

const HeadingButton = styled(Button)`
  padding: 0;
  cursor: pointer;
  outline: none;
  user-select: none;
`;

const CandidateDetails: React.FC<any> = ({ match, history }): JSX.Element => {
  const {
    role,
    candidate,
    openModalHandler,
    messagesCounter,
    downloadAttachedFileHandler,
    candidateNote,
  } = useCandidateDetails(+match.params.id);
  return (
    <>
      {candidate && (
        <Flex>
          <Frame>
            <Flex width="100%" flexDirection="column" pt={7}>
              <Flex>
                <HeadingButton
                  color={colors.lightGreen}
                  bg="transparent"
                  mb={4}
                  onClick={() => history.goBack()}
                >
                  <Flex>
                    <Text fontSize="18px" fontWeight={4}>
                      {'< Back'}
                    </Text>
                  </Flex>
                </HeadingButton>
              </Flex>
              <Flex width="100%" justifyContent="space-between" alignItems="center" mb={7}>
                <Heading fontSize="36px" fontWeight={6}>
                  {candidate.firstName} {candidate.lastName}
                </Heading>
                <Flex
                  css="cursor: pointer"
                  onClick={openModalHandler({
                    title: 'sendMessages',
                    data: {
                      candidateId: candidate.id,
                      haveMessages: true,
                    },
                  })}
                >
                  <Icon type="note" width="35px" height="35px" />
                  <Text fontSize={3} fontWeight={6} mt="5px" mx={1}>
                    {messagesCounter}
                  </Text>
                  <Text fontSize={3} fontWeight={6} mt="5px">
                    Notes History
                  </Text>
                </Flex>
              </Flex>
              <Flex width="100%" flexDirection="column">
                <Flex width="100%">
                  <Flex width="100%">
                    <Flex width="100%" flexDirection="column" mr={9}>
                      <Text fontSize="20px" fontWeight={5} mb={2}>
                        Personal Info
                      </Text>
                      {role !== EMPLOYER && (
                        <>
                          <Flex fontSize="18px">
                            Email:
                            <Text fontSize="18px" fontWeight={5} mb={2}>
                              {` ${candidate.email}`}
                            </Text>
                          </Flex>
                          <Flex fontSize="18px">
                            Contact number:
                            <Text fontSize="18px" fontWeight={5} mb={2}>
                              {` ${candidate.phoneNumber}`}
                            </Text>
                          </Flex>
                        </>
                      )}
                      <Flex fontSize="18px" mb={2}>
                        City:
                        <Text fontSize="18px" fontWeight={5}>
                          {` ${candidate.city}`}
                        </Text>
                      </Flex>
                      {candidate.cvUrl && (
                        <Flex alignItems="center" mb={2}>
                          <Text fontSize="18px" mr={1}>
                            Resume:
                          </Text>
                          <Icon type="attachFile" color="black" />
                          <Text
                            ml={1}
                            fontWeight={6}
                            css="cursor: pointer; text-decoration: underline;"
                            onClick={downloadAttachedFileHandler('cv', candidate.id)}
                          >
                            {candidate.cvUrl}
                          </Text>
                        </Flex>
                      )}
                      {candidate.unsignedOfferUrl && (
                        <Flex alignItems="center" mb={2}>
                          <Text fontSize="18px" mr={1}>
                            Job Offer:
                          </Text>
                          <Icon type="attachFile" color="black" />
                          <Text
                            ml={1}
                            fontWeight={6}
                            css="cursor: pointer; text-decoration: underline;"
                            onClick={downloadAttachedFileHandler('unsignedOffer', candidate.id)}
                          >
                            {candidate.unsignedOfferUrl}
                          </Text>
                        </Flex>
                      )}
                      {candidate.signedOfferUrl && (
                        <Flex alignItems="center" mb={2}>
                          <Text fontSize="18px" mr={1}>
                            Accepted Job Offer:
                          </Text>
                          <Icon type="attachFile" color="black" />
                          <Text
                            ml={1}
                            fontWeight={6}
                            css="cursor: pointer; text-decoration: underline;"
                            onClick={downloadAttachedFileHandler('signedOffer', candidate.id)}
                          >
                            {candidate.signedOfferUrl}
                          </Text>
                        </Flex>
                      )}
                      {candidate.invoiceUrl && (
                        <Flex alignItems="center" mb={2}>
                          <Text fontSize="18px" mr={1}>
                            Invoice:
                          </Text>
                          <Icon type="attachFile" color="black" />
                          <Text
                            ml={1}
                            fontWeight={6}
                            css="cursor: pointer; text-decoration: underline;"
                            onClick={downloadAttachedFileHandler('invoice', candidate.id)}
                          >
                            {candidate.invoiceUrl}
                          </Text>
                        </Flex>
                      )}
                      <Flex>
                        {candidate.linkedInUrl && (
                          <Link href={candidate.linkedInUrl}>
                            <Image src={candidateLinkedInIcon} mr={2} />
                          </Link>
                        )}
                        {candidate.twitterUrl && (
                          <Link href={candidate.twitterUrl}>
                            <Image src={candidateTwitterIcon} mr={2} />
                          </Link>
                        )}
                        {candidate.gitHubUrl && (
                          <Link href={candidate.gitHubUrl}>
                            <Image src={candidateGitHubIcon} mr={2} />
                          </Link>
                        )}
                        {candidate.website && (
                          <Link href={candidate.website}>
                            <Image src={candidateWebsiteIcon} mr={2} />
                          </Link>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex width="100%" css="max-width: 500px;">
                    <Flex width="100%" flexDirection="column">
                      <Text fontSize="20px" fontWeight={5} mb={2}>
                        Skills
                      </Text>
                      <Box>
                        <Text fontSize="18px" mb={1}>
                          Summary:
                        </Text>
                        <Text fontSize="18px" fontWeight={5} mb={2}>
                          {candidate.summary}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="18px" mb={1}>
                          Top three skills:
                        </Text>
                        <Flex width="100%" mb={2} css="max-width: 350px;">
                          {candidate.skills.map((skill: string) => (
                            <SkillBox key={skill} py={1} mr={1}>
                              {skill}
                            </SkillBox>
                          ))}
                        </Flex>
                      </Box>
                      {(candidate.salary || candidate.salary >= 0) && (
                        <Flex fontSize="18px">
                          Desired salary:
                          <Text fontSize="18px" fontWeight={5} mb={2}>
                            {` $${candidate.salary}`}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
                {candidateNote && (
                  <Flex flexDirection="column" mt={2}>
                    <Text fontSize="20px" fontWeight={5} mb={2}>
                      Recruiter Notes
                    </Text>
                    <Text css="max-width: 300px;">{candidateNote.message}</Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Frame>
        </Flex>
      )}
    </>
  );
};

export default CandidateDetails;
